import { openWithTenant } from '@app-store'
import { Modal, CustomButton, Tabs, TabInterface } from '@ui-kit'
import { ListingTable } from '../listing-table/ListingTable'
import { DuplicatePopupModalProps } from '../../utils/types'
import { ListingTableProps } from '../../types/listingTable.types'
import { useState, useEffect, useMemo, memo, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  RootState,
  store,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import {
  attachCaseById,
  patchAttachForHcpEntity,
  fetchTypeStatusForCommunications,
  fetchDocumentIdForHcpEntity,
  fetchCaseDocumentIdForHcpEntity,
  addToaster,
  hideLoader,
  showLoader,
  useTenantNavigate,
} from '@app-store'
import {
  COMM_TEMPLATE,
  DUPLICATE_POPUP,
  DuplicatePopupModalStep,
  ENTITY_NAME,
  NAMESPACES,
} from '../../utils/constants'
import {
  AttachCaseConfirmDialog,
  ConfirmDialogHandle,
  ProceedAnywayConfirmDialog,
} from './DedupeConfirmDialogs'
import { DedupeOutcomeDetailsStep } from './DedupeOutcomeDetailsStep'
import { useCloseDedupeCaseEnabled } from './dedupe-close-case/useCloseDedupeCaseEnabled'
import { executeDedupeOutcomeSaveFlow } from './dedupe-close-case/executeDedupeOutcomeSaveFlow'
import { v4 as uuid } from 'uuid'
import { QUEUE_STRINGS } from '../../utils/queueIntake.types'
import {
  fetchEntityIdByName,
  fetchLayoutIdByEntityId,
} from '../../utils/helperFunctions'
import { STRINGS } from '../../utils/strings'
import { caseCancelOrVoidApi } from '../../apis/caseStatus.api'
import {
  editEntityData,
  editEntityDataEmbeddedAPI,
  getEntityDataByIdEmbedded,
} from '../../apis/entityData.api'
import { FieldValues } from 'react-hook-form'
import { loadCaseWorkflow } from '../../pages/intake-ques/multi-product/loadCaseWorkflow'
import { AttachCaseConfirmDialogHandle } from '../../types/multiProduct.types'

export const DuplicatePopupModal = memo(
  ({
    isOpen,
    embedded = false,
    title = '',
    totalResults = 0,
    dataFetchCallback,
    entityId,
    entityName,
    layout,
    extraColumns = [],
    extraActions = [],
    onProceedAnyway,
    onCancel,
    closeModalOnOutsideClick = true,
    isLoading = false,
    updateStatusToProcessing,
    updateStatusToCompleted,
    communicationId,
    faxDetails,
    intakeEntityName,
    fromInteraction,
    proceedButtonText,
    isMultiCaseEnabled = false,
    childCases = [],
    tabsList: tabsListProp = [],
    activeChildCaseId,
    onChildCaseTabChange,
    onRemoveChildCaseTab,
    refetchEnrollment,
    onMultiProductCaseComplete,
    definition,
    instance,
    selectedChannelId,
  }: DuplicatePopupModalProps) => {
    const dispatch = useAppDispatch()
    const navigate = useTenantNavigate()

    const intakeCommunicationMap = useAppSelector(
      (state: RootState) => state.workflowData?.intakeCommunicationMap
    )
    const schemaName = useAppSelector(
      (state: RootState) => state.authUser.selectedClientTenant?.schema_name
    )
    const {
      entityid,
      layoutid,
      id: queueId,
    } = useParams<{
      entityid?: string
      layoutid?: string
      id?: string
    }>()
    const authUser = useAppSelector((state) => state.authUser)
    const { shouldShowCloseDedupeFlow } = useCloseDedupeCaseEnabled(embedded)
    const attachConfirmRef = useRef<AttachCaseConfirmDialogHandle>(null)
    const proceedConfirmRef = useRef<ConfirmDialogHandle>(null)
    const [modalStep, setModalStep] = useState<DuplicatePopupModalStep>(
      DUPLICATE_POPUP.MODAL_STEP.DUPLICATES
    )
    const [pendingAttachCaseId, setPendingAttachCaseId] = useState<
      string | number | null
    >(null)
    const [isSavingOutcomeDetails, setIsSavingOutcomeDetails] = useState(false)
    const [selectedTabKey, setSelectedTabKey] = useState<string | null>(null)
    const selectedTabKeyRef = useRef<string | null>(null)
    selectedTabKeyRef.current = selectedTabKey
    const tabsListPropRef = useRef(tabsListProp)
    tabsListPropRef.current = tabsListProp
    const categoryData = useAppSelector(
      (state) => state?.categoryDropdown?.categoryMap
    )
    const getCaseDocumentTypeId = () =>
      categoryData?.[COMM_TEMPLATE.CASE_DOCUMENT_TYPE]?.find(
        (item) => item.value === DUPLICATE_POPUP.INTAKE_FORM
      )?.id

    // Read workflow from the store on demand (attach/save). Do not subscribe —
    // tab switches dispatch definition/instance updates that would re-render this
    // modal and flicker the listing while Redux is updated.
    const getWorkflowSnapshot = useCallback(() => {
      const workflowData = store.getState().workflowData
      return {
        workflowInstance: instance || workflowData?.workflowInstance,
        workflowDefinition: definition || workflowData?.workflowDefinition,
        caseData: workflowData?.caseData as FieldValues | undefined,
      }
    }, [instance, definition])

    const activeChildCase = useMemo(() => {
      if (!isMultiCaseEnabled || !childCases.length) return undefined
      const preferredKey =
        activeChildCaseId ?? childCases[0]?.case?.case_key ?? null
      return (
        childCases.find((item) => item?.case?.case_key === preferredKey) ??
        childCases[0]
      )
    }, [isMultiCaseEnabled, childCases, activeChildCaseId])

    const resolveCaseIdForTab = useCallback(
      (caseKey: string, tabs: TabInterface[] = tabsListProp) => {
        const tab = tabs.find(
          (item) => String(item.key ?? item.label ?? '') === caseKey
        )
        if (tab?.id != null && tab.id !== '') {
          return String(tab.id)
        }

        const childCase = childCases.find(
          (item) => item?.case?.case_key === caseKey
        )
        const resolvedId = childCase?.case_id ?? childCase?.case?.id
        return resolvedId != null ? String(resolvedId) : null
      },
      [childCases, tabsListProp]
    )

    const loadWorkflowForTab = useCallback(
      async (caseKey: string, tabs?: TabInterface[]) => {
        if (!isMultiCaseEnabled || !caseKey) return
        const resolvedCaseId = resolveCaseIdForTab(caseKey, tabs)
        if (!resolvedCaseId) return
        await loadCaseWorkflow(dispatch, resolvedCaseId, { silent: true })
      },
      [dispatch, isMultiCaseEnabled, resolveCaseIdForTab]
    )

    const {
      caseData: snapshotCaseData,
      workflowDefinition: snapshotDefinition,
    } = getWorkflowSnapshot()
    const serviceId =
      activeChildCase?.case?.service_id ??
      snapshotCaseData?.services?.id ??
      snapshotDefinition?.service_id
    const subServiceId =
      definition?.global_variables?.inherited_variables?.sub_service_id ??
      activeChildCase?.case?.sub_service_id ??
      snapshotCaseData?.c_sub_services?.id ??
      snapshotDefinition?.global_variables?.inherited_variables
        ?.sub_service_id ??
      snapshotDefinition?.definition?.processVariables?.inherited_variables
        ?.sub_service_id
    const [layoutIdForHcpEntity, setLayoutIdForHcpEntity] = useState<number>()

    const resolvedIntakeChannelId =
      Number(
        intakeCommunicationMap?.[selectedChannelId ?? '0'] ||
          intakeCommunicationMap?.[String(faxDetails?.channel?.id)] ||
          faxDetails?.hcp_enrollment_details?.[0]?.case?.intake_channel?.id
      ) || null

    const fetchWebEnrollmentStatusId = async (): Promise<
      string | undefined
    > => {
      if (childCases.length) return
      const response = await fetchTypeStatusForCommunications(
        NAMESPACES.WEB_ENROLLMENT_STATUS
      )
      return response?.data?.find(
        (item: { value: string }) =>
          item?.value?.toLowerCase() === NAMESPACES?.PROCESSING_LOWERCASE
      )?.id
    }

    const fetchCaseDocumentId = async (
      id: number | string
    ): Promise<string | undefined> => {
      const entityData = await fetchEntityIdByName(
        dispatch,
        NAMESPACES?.WEB_ENROLLMENT_DOCUMENT
      )
      if (!entityData) return

      const documents = await fetchDocumentIdForHcpEntity(
        entityData?.id.toString() ?? '',
        faxDetails?.enrollment_id ?? ''
      )

      const matchedDoc = documents?.data?.content?.find(
        (item: { enrollment_id: string }) =>
          item?.enrollment_id === faxDetails?.enrollment_id
      )

      if (!matchedDoc?.document?.id) return

      const caseDocEntity = await fetchEntityIdByName(
        dispatch,
        NAMESPACES?.CASE_DOCUMENTS
      )
      if (!caseDocEntity) return

      const layout = await fetchLayoutIdByEntityId(
        dispatch,
        caseDocEntity.id,
        QUEUE_STRINGS?.EDIT_MODE
      )
      const layoutId = layout?.find(
        (item: { entity_name: string }) =>
          item?.entity_name === NAMESPACES.CASE_DOCUMENTS
      )?.id

      if (!layoutId) return

      const caseDocumentTypeId = getCaseDocumentTypeId()
      if (!caseDocumentTypeId) return

      const caseDocument = await fetchCaseDocumentIdForHcpEntity(
        caseDocEntity.id.toString(),
        layoutId.toString(),
        id,
        matchedDoc.document.id.toString(),
        faxDetails?.enrollment_id,
        caseDocumentTypeId
      )

      return caseDocument?.data?.id
    }

    const showSuccessToast = () => {
      dispatch(
        addToaster({
          message: QUEUE_STRINGS?.CASE_ATTACHED,
          type: 'success',
          id: uuid(),
        })
      )
    }

    const handleHcpEntityAttachment = async (id: string | number) => {
      const statusId = await fetchWebEnrollmentStatusId()
      if (!statusId) return

      const attached = await patchAttachForHcpEntity(
        [communicationId ?? ''],
        entityId.toString() ?? '',
        layoutIdForHcpEntity?.toString() ?? '',
        id,
        statusId
      )

      if (!attached) return

      showSuccessToast()

      await fetchCaseDocumentId(id)
    }

    const handleDefaultEntityAttachment = async (
      id: string | number,
      reason: string,
      reviewer: string
    ) => {
      const res = await attachCaseById({
        faxId: communicationId ?? '',
        caseId: id,
        reason,
        reviewer,
      })

      showSuccessToast()

      const caseDocEntity = await fetchEntityIdByName(
        dispatch,
        NAMESPACES?.CASE_DOCUMENTS
      )
      if (!caseDocEntity) return

      const layout = await fetchLayoutIdByEntityId(
        dispatch,
        caseDocEntity.id,
        QUEUE_STRINGS?.EDIT_MODE
      )
      const layoutId = layout?.find(
        (item: { entity_name: string }) =>
          item?.entity_name === NAMESPACES?.CASE_DOCUMENTS
      )?.id

      if (!layoutId) return

      const caseDocumentTypeId = getCaseDocumentTypeId()
      if (!caseDocumentTypeId) return

      await editEntityData(
        NAMESPACES?.CASE_DOCUMENTS,
        caseDocEntity.id.toString(),
        layoutId.toString(),
        {
          case_document_type: {
            id: caseDocumentTypeId,
            _state: NAMESPACES?.CHANGED,
          },
        },
        res?.data?.document_entities?.id,
        []
      )
    }

    const setCaseCompletionDateApi = async () => {
      try {
        const { workflowDefinition, workflowInstance } = getWorkflowSnapshot()
        await editEntityDataEmbeddedAPI(
          workflowDefinition?.global_variables?.entity_name ?? '',
          Number(workflowInstance?.case_id),
          { case_completion_date: new Date().toISOString() },
          ['case_completion_date']
        )
      } catch (error) {
        console.error(error)
      }
    }

    const closeCurrentCaseAfterAttach = async () => {
      try {
        dispatch(showLoader())
        const { workflowDefinition, workflowInstance } = getWorkflowSnapshot()
        await caseCancelOrVoidApi(
          workflowInstance?.id?.toString(),
          STRINGS.CANCEL
        )
        const response = await getEntityDataByIdEmbedded(
          workflowDefinition?.global_variables?.entity_name ?? '',
          Number(workflowDefinition?.global_variables?.entity_id),
          ['case_completion_date'],
          Number(workflowInstance?.case_id)
        )
        if (!response?.data?.case_completion_date) {
          await setCaseCompletionDateApi()
        }
      } catch (error) {
        console.error(error)
        dispatch(
          addToaster({
            message: STRINGS?.SOMETHING_WENT_WRONG,
            type: 'error',
            id: uuid(),
          })
        )
      } finally {
        dispatch(hideLoader())
      }
    }

    const attachCaseForCloseDedupeFlow = async (
      id: string | number,
      reason: string
    ) => {
      const reviewer = `${authUser?.user?.first_name} ${authUser?.user?.last_name}`
      if (intakeEntityName === QUEUE_STRINGS?.HCP_PORTAL_ENTITY_LOWERCASE) {
        await handleHcpEntityAttachment(id)
      } else {
        await handleDefaultEntityAttachment(id, reason, reviewer)
      }
    }

    const onSubmitAttach = async (id: string | number) => {
      try {
        dispatch(showLoader())
        const reviewer = `${authUser?.user?.first_name} ${authUser?.user?.last_name}`
        if (intakeEntityName === QUEUE_STRINGS?.HCP_PORTAL_ENTITY_LOWERCASE) {
          await handleHcpEntityAttachment(id)
        } else {
          await handleDefaultEntityAttachment(
            id,
            NAMESPACES.NO_REASON,
            reviewer
          )
        }
        //TODO: TO REMOVE THIS ONE FOR MULTIPRODUCT
        if (!isMultiCaseEnabled) await updateStatusToProcessing?.(Number(id))
      } catch (err) {
        console.error(err)
      } finally {
        await closeCurrentCaseAfterAttach()
        openWithTenant(
          `/entity/32/workflow/review/${id}?tabs=Correspondence`,
          '_self'
        )
        dispatch(hideLoader())
      }
    }

    const resetOutcomeDetailsStep = useCallback(() => {
      setModalStep(DUPLICATE_POPUP.MODAL_STEP.DUPLICATES)
      setPendingAttachCaseId(null)
      setIsSavingOutcomeDetails(false)
    }, [])

    const handleAttachClick = (row: FieldValues) => {
      if (shouldShowCloseDedupeFlow) {
        attachConfirmRef.current?.open(row?.id)
        return
      }

      onSubmitAttach(row?.id)
    }

    const cancelActiveWorkflowProcess = useCallback(async () => {
      const { workflowInstance } = getWorkflowSnapshot()
      const workflowInstanceId = workflowInstance?.id?.toString()
      if (!workflowInstanceId) return

      await caseCancelOrVoidApi(workflowInstanceId, STRINGS.CANCEL)
    }, [getWorkflowSnapshot])

    const updateWorkflowProceededForCurrentCase = async () => {
      try {
        if (childCases?.length) {
          // Find the enrollment details record
          const enrollmentDetailsRecord = childCases?.find(
            (item: FieldValues) =>
              item?.case_id?.toString() === defaultActiveTab?.id?.toString()
          )?.id

          const data = {
            workflow_proceeded: true,
            _state: 'CHANGED',
          }

          await editEntityDataEmbeddedAPI(
            ENTITY_NAME.HCP_ENROLLMENT_DETAILS,
            enrollmentDetailsRecord ?? '',
            data,
            []
          )
        }
      } catch (err) {
        console.error(err)
      }
    }

    const handleOutcomeDetailsSave = useCallback(
      async ({
        outcomeId,
        outcomeReasonId,
        outcomeReasonLabel,
      }: {
        outcomeId: string
        outcomeReasonId: string
        outcomeReasonLabel: string
      }) => {
        if (
          !shouldShowCloseDedupeFlow ||
          modalStep !== DUPLICATE_POPUP.MODAL_STEP.OUTCOME_DETAILS ||
          pendingAttachCaseId == null
        ) {
          return
        }

        setIsSavingOutcomeDetails(true)

        try {
          dispatch(showLoader())
          const { workflowDefinition, workflowInstance, caseData } =
            getWorkflowSnapshot()
          const caseId = workflowInstance?.case_id
          const activeKey = selectedTabKeyRef.current
          const activeTab = tabsListPropRef.current.find(
            (tab) => String(tab.key ?? tab.label ?? '') === String(activeKey)
          )
          const originalCaseId =
            activeTab?.id ??
            activeChildCase?.case_id ??
            activeChildCase?.case?.id ??
            caseId

          if (isMultiCaseEnabled) {
            await updateWorkflowProceededForCurrentCase()
          }
          await executeDedupeOutcomeSaveFlow({
            attachedCaseId: pendingAttachCaseId,
            outcomeId,
            outcomeReasonId,
            outcomeReasonLabel,
            attachCase: attachCaseForCloseDedupeFlow,
            // Multi-product: complete by cancelling the active child-case process
            updateStatusToCompleted:
              isMultiCaseEnabled || !!onMultiProductCaseComplete
                ? cancelActiveWorkflowProcess
                : updateStatusToCompleted,
            faxDetails,
            communicationId,
            caseDocumentTypeId: getCaseDocumentTypeId(),
            assigneeUserId: Number(authUser?.user?.id),
            workflowInstanceId: workflowInstance?.id?.toString(),
            caseId: isMultiCaseEnabled ? originalCaseId : caseId,
            workflowDefinition,
            navigate,
            queueParams: { entityid, layoutid, id: queueId },
            requestorId: caseData?.requestor_view?.id ?? null,
            schemaName: schemaName ?? '',
            onTaskCreationError: () => {
              dispatch(
                addToaster({
                  message: STRINGS.ERROR_CREATING_TASK,
                  type: 'error',
                  id: uuid(),
                })
              )
            },
            redirectOnSave: !isMultiCaseEnabled,
            createNewAttachmentTask: !isMultiCaseEnabled,
            // Cancel already ran via updateStatusToCompleted for multi-product
            skipCloseProcessApi: isMultiCaseEnabled,
            closeProcess: isMultiCaseEnabled ? 'cancel' : undefined,
            onMultiProductCaseComplete,
            fromInteraction,
            intakeCommunicationMap,
            intakeChannelId: resolvedIntakeChannelId,
            intakeEntityName,
          })
          if (onMultiProductCaseComplete) {
            await onMultiProductCaseComplete()
            await refetchEnrollment?.()
          }
          if (isMultiCaseEnabled) {
            if (!activeKey) return

            const remainingTabs = tabsListPropRef.current.filter(
              (tab) => String(tab.key ?? tab.label ?? '') !== activeKey
            )
            await onRemoveChildCaseTab?.(remainingTabs)

            const url = `/entity/32/workflow/review/${pendingAttachCaseId}`
            await openWithTenant(url, '_blank')

            await resetOutcomeDetailsStep()
            if (!remainingTabs.length) {
              await onProceedAnyway?.(true)
              await refetchEnrollment?.()
              return
            }

            await refetchEnrollment?.()

            const nextKey = String(
              remainingTabs[0]?.key ?? remainingTabs[0]?.label ?? ''
            )
            setSelectedTabKey(nextKey)
            await onChildCaseTabChange?.(nextKey)
            await loadWorkflowForTab(nextKey, remainingTabs)
          }
        } catch (error) {
          console.error(error)
          dispatch(
            addToaster({
              message: STRINGS.SOMETHING_WENT_WRONG,
              type: 'error',
              id: uuid(),
            })
          )
        } finally {
          dispatch(hideLoader())
          setIsSavingOutcomeDetails(false)
        }
      },
      [
        isMultiCaseEnabled,
        onRemoveChildCaseTab,
        onProceedAnyway,
        onChildCaseTabChange,
        resetOutcomeDetailsStep,
        attachCaseForCloseDedupeFlow,
        authUser?.user?.id,
        authUser?.selectedClientTenant?.id,
        activeChildCase,
        cancelActiveWorkflowProcess,
        communicationId,
        dispatch,
        entityid,
        faxDetails,
        getWorkflowSnapshot,
        layoutid,
        loadWorkflowForTab,
        modalStep,
        navigate,
        pendingAttachCaseId,
        queueId,
        shouldShowCloseDedupeFlow,
        updateStatusToCompleted,
      ]
    )

    const handleProceedAnywayClick = useCallback(() => {
      if (shouldShowCloseDedupeFlow) {
        proceedConfirmRef.current?.open()
        return
      }

      onProceedAnyway?.()
    }, [shouldShowCloseDedupeFlow, onProceedAnyway])

    const handleProceedAnywayConfirmed = useCallback(() => {
      onProceedAnyway?.()
    }, [onProceedAnyway])

    const handleCancelClick = useCallback(() => {
      if (attachConfirmRef.current?.isOpen()) {
        attachConfirmRef.current.dismiss()
        return
      }

      if (proceedConfirmRef.current?.isOpen()) {
        proceedConfirmRef.current.dismiss()
        return
      }

      if (modalStep === DUPLICATE_POPUP.MODAL_STEP.OUTCOME_DETAILS) {
        resetOutcomeDetailsStep()
        return
      }

      onCancel()
    }, [modalStep, onCancel, resetOutcomeDetailsStep])

    useEffect(() => {
      if (!isOpen) {
        resetOutcomeDetailsStep()
      }
    }, [isOpen, resetOutcomeDetailsStep])

    useEffect(() => {
      const fetchLayoutIdForHcpEntity = async () => {
        const response = await fetchLayoutIdByEntityId(
          dispatch,
          parseInt(entityid ?? ''),
          QUEUE_STRINGS?.EDIT_MODE
        )
        const layoutIdForHcpEntity = response?.find(
          (item: { entity_name: string }) =>
            item.entity_name === QUEUE_STRINGS.HCP_PORTAL_ENTITY_LOWERCASE
        )?.id
        layoutIdForHcpEntity && setLayoutIdForHcpEntity(layoutIdForHcpEntity)
      }
      if (
        entityid &&
        intakeEntityName === QUEUE_STRINGS.HCP_PORTAL_ENTITY_LOWERCASE
      ) {
        fetchLayoutIdForHcpEntity()
      }
    }, [entityid, dispatch, intakeEntityName])

    const listingExtraActions = useMemo(() => {
      const hasAttachKeyAction = extraActions.some(
        (item) =>
          item.key === DUPLICATE_POPUP?.ATTACH_KEY ||
          item.key === DUPLICATE_POPUP?.LINK_KEY
      )

      if (!hasAttachKeyAction) {
        return extraActions
      }

      return extraActions.map((item) =>
        item.key === DUPLICATE_POPUP?.ATTACH_KEY ||
        item.key === DUPLICATE_POPUP?.LINK_KEY
          ? {
              ...item,
              onClick: (row: FieldValues) => handleAttachClick(row),
            }
          : item
      )
    }, [extraActions, intakeEntityName, layoutIdForHcpEntity, communicationId])

    const dataFetchCallbackRef = useRef(dataFetchCallback)
    dataFetchCallbackRef.current = dataFetchCallback
    const listingExtraActionsRef = useRef(listingExtraActions)
    listingExtraActionsRef.current = listingExtraActions

    useEffect(() => {
      if (!isOpen) {
        setSelectedTabKey(null)
        return
      }

      if (selectedTabKey != null) return

      const initialKey =
        activeChildCaseId ??
        String(tabsListProp[0]?.key ?? tabsListProp[0]?.label ?? '')
      if (initialKey) {
        setSelectedTabKey(initialKey)
        void loadWorkflowForTab(initialKey)
      }
    }, [
      isOpen,
      activeChildCaseId,
      tabsListProp,
      selectedTabKey,
      loadWorkflowForTab,
    ])

    const renderDuplicatesTable = useCallback(
      (childCaseKey?: string | null) => (
        <div className="mt-2.5 border rounded-md p-2 flex flex-col flex-1 min-h-0">
          <ListingTable
            entityId={entityId}
            entityName={entityName}
            layout={layout}
            dataFetchCallback={
              ((payload) => {
                const callback = dataFetchCallbackRef.current
                if (!callback) {
                  return Promise.resolve({ content: [], totalPages: 0 })
                }
                return callback(payload, childCaseKey)
              }) as ListingTableProps['dataFetchCallback']
            }
            extraColumns={extraColumns}
            extraActions={listingExtraActionsRef.current}
            hideFilters={true}
            openInNewTab={true}
            isLoading={isLoading}
            isSortable={false}
            minHeight="7rem"
          />
        </div>
      ),
      [entityId, entityName, layout, extraColumns, isLoading]
    )

    const tabsList: TabInterface[] = useMemo(
      () =>
        tabsListProp.map((tab) => {
          const caseKey = String(tab.key ?? tab.label ?? '')
          return {
            ...tab,
            component: renderDuplicatesTable(caseKey),
          }
        }),
      [tabsListProp, renderDuplicatesTable]
    )

    const defaultActiveTab = useMemo(() => {
      if (!tabsList.length) return undefined
      return (
        tabsList.find((tab) => String(tab.key ?? '') === selectedTabKey) ??
        tabsList[0]
      )
    }, [tabsList, selectedTabKey])

    const handleAttachConfirmed = useCallback(
      async (caseId: string | number) => {
        setPendingAttachCaseId(caseId)
        setModalStep(DUPLICATE_POPUP.MODAL_STEP.OUTCOME_DETAILS)
      },
      [childCases, defaultActiveTab]
    )

    const handleChildCaseTabChange = useCallback(
      (_label: string, index: number) => {
        const caseKey = String(
          tabsList[index]?.key ?? tabsList[index]?.label ?? ''
        )
        if (!caseKey) return
        setSelectedTabKey(caseKey)
        onChildCaseTabChange?.(caseKey)
        void loadWorkflowForTab(caseKey)
      },
      [tabsList, onChildCaseTabChange, loadWorkflowForTab]
    )

    if (!embedded && !isOpen) {
      return null
    }

    const duplicatesContent = (
      <div className="bg-accent-0 flex flex-col h-full">
        <div className="flex gap-2 justify-between">
          <div>
            <div className="flex justify-between items-center pb-2">
              <div className="flex items-baseline gap-2">
                <div className="text-sm font-primary font-medium">{title}</div>
                <div className="text-xs font-primary font-light">
                  {totalResults} {DUPLICATE_POPUP.RESULT}
                  {totalResults !== 1 && 's'} {DUPLICATE_POPUP.FOUND}
                </div>
              </div>
            </div>
            <div className="text-xs font-primary font-light">
              {DUPLICATE_POPUP.SELECTING}{' '}
              <span className="font-medium">
                {fromInteraction ? DUPLICATE_POPUP.LINK : NAMESPACES.ATTACH}
              </span>{' '}
              {fromInteraction
                ? DUPLICATE_POPUP.LINK_SUBTITLE_MSG
                : DUPLICATE_POPUP.ATTACH_SUBTITLE_MSG}
            </div>
          </div>
          <div className="flex gap-2.5">
            <CustomButton onClick={handleCancelClick}>
              {NAMESPACES?.CANCEL_BUTTON}
            </CustomButton>
            <CustomButton isFilled onClick={handleProceedAnywayClick}>
              {proceedButtonText || DUPLICATE_POPUP?.PROCEED_ANYWAY}
            </CustomButton>
          </div>
        </div>
        {tabsList.length ? (
          <Tabs
            tabs={tabsList}
            onChange={handleChildCaseTabChange}
            defaultActive={defaultActiveTab}
            tabsWrapperClassName="flex-1"
            scrollIntoViewOnLoad={false}
          />
        ) : (
          renderDuplicatesTable()
        )}
      </div>
    )

    const showOutcomeDetails =
      shouldShowCloseDedupeFlow &&
      modalStep === DUPLICATE_POPUP.MODAL_STEP.OUTCOME_DETAILS

    if (embedded) {
      return duplicatesContent
    }

    return (
      <>
        <Modal
          isOpen={isOpen}
          closeModal={handleCancelClick}
          width="min-w-[60vw] max-w-[70vw]"
          bodyCssClassName="h-[30rem]"
          showCloseButton={false}
          closeModalOnOutsideClick={
            showOutcomeDetails ? false : closeModalOnOutsideClick
          }
          isDragable
          isResizable
          isBackdropInteractive
          minResizeWidth={480}
          minResizeHeight={260}
        >
          {showOutcomeDetails ? (
            <DedupeOutcomeDetailsStep
              serviceId={serviceId}
              subServiceId={subServiceId}
              onBack={resetOutcomeDetailsStep}
              onCancel={handleCancelClick}
              onSave={handleOutcomeDetailsSave}
              isSaving={isSavingOutcomeDetails}
              isOutcomeSelectionDisabled={true}
            />
          ) : (
            duplicatesContent
          )}
        </Modal>

        {shouldShowCloseDedupeFlow && (
          <>
            <AttachCaseConfirmDialog
              ref={attachConfirmRef}
              onConfirm={handleAttachConfirmed}
              isMultiCaseEnabled
              fromInteraction
            />
            <ProceedAnywayConfirmDialog
              ref={proceedConfirmRef}
              onConfirm={handleProceedAnywayConfirmed}
            />
          </>
        )}
      </>
    )
  }
)

DuplicatePopupModal.displayName = 'DuplicatePopupModal'
